import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

const noiseFunctions = `
vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
vec4 mod289(vec4 x){return x-floor(x*(1./289.))*289.;}
vec4 permute(vec4 x){return mod289(((x*34.)+1.)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1./6.,1./3.);const vec4 D=vec4(0.,.5,1.,2.);
  vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.-g;
  vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(i.z+vec4(0.,i1.z,i2.z,1.))+i.y+vec4(0.,i1.y,i2.y,1.))+i.x+vec4(0.,i1.x,i2.x,1.));
  float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.*x_);
  vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.+1.;vec4 s1=floor(b1)*2.+1.;vec4 sh=-step(h,vec4(0.));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);m=m*m;
  return 42.*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}`;

const nodeVertexShader = `${noiseFunctions}
attribute float nodeSize;
attribute float nodeType;
attribute vec3 nodeColor;
attribute float distanceFromRoot;
uniform float uTime;
uniform vec3 uPulsePositions[3];
uniform float uPulseTimes[3];
uniform float uPulseSpeed;
uniform float uBaseNodeSize;
varying vec3 vColor;
varying float vNodeType;
varying vec3 vPosition;
varying float vPulseIntensity;
varying float vDistanceFromRoot;
varying float vGlow;
float getPulseIntensity(vec3 worldPos,vec3 pulsePos,float pulseTime){
  if(pulseTime<0.)return 0.;
  float age=uTime-pulseTime;
  if(age<0.||age>4.)return 0.;
  float radius=age*uPulseSpeed;
  float d=distance(worldPos,pulsePos);
  return smoothstep(3.,0.,abs(d-radius))*smoothstep(4.,0.,age);
}
void main(){
  vNodeType=nodeType;vColor=nodeColor;vDistanceFromRoot=distanceFromRoot;
  vec3 worldPos=(modelMatrix*vec4(position,1.)).xyz;
  vPosition=worldPos;
  float total=0.;
  for(int i=0;i<3;i++) total+=getPulseIntensity(worldPos,uPulsePositions[i],uPulseTimes[i]);
  vPulseIntensity=min(total,1.);
  float breathe=sin(uTime*.7+distanceFromRoot*.15)*.15+.85;
  float sz=nodeSize*breathe*(1.+vPulseIntensity*2.5);
  vGlow=.5+.5*sin(uTime*.5+distanceFromRoot*.2);
  vec3 pos=position;
  if(nodeType>.5){float noise=snoise(position*.08+uTime*.08);pos+=normal*noise*.15;}
  vec4 mvPos=modelViewMatrix*vec4(pos,1.);
  gl_PointSize=sz*uBaseNodeSize*(1000./-mvPos.z);
  gl_Position=projectionMatrix*mvPos;
}`;

const nodeFragmentShader = `
uniform float uTime;
uniform vec3 uPulseColors[3];
varying vec3 vColor;
varying float vNodeType;
varying vec3 vPosition;
varying float vPulseIntensity;
varying float vDistanceFromRoot;
varying float vGlow;
void main(){
  vec2 center=2.*gl_PointCoord-1.;
  float dist=length(center);
  if(dist>1.)discard;
  float glow1=1.-smoothstep(0.,.5,dist);
  float glow2=1.-smoothstep(0.,1.,dist);
  float gs=pow(glow1,1.2)+glow2*.3;
  float breatheColor=.9+.1*sin(uTime*.6+vDistanceFromRoot*.25);
  vec3 col=vColor*breatheColor;
  if(vPulseIntensity>0.){
    vec3 pc=mix(vec3(1.),uPulseColors[0],.4);
    col=mix(col,pc,vPulseIntensity*.8);
    col*=(1.+vPulseIntensity*1.2);
    gs*=(1.+vPulseIntensity);
  }
  col+=vec3(1.)*smoothstep(.4,0.,dist)*.3;
  float alpha=gs*(.95-.3*dist);
  float camDist=length(vPosition-cameraPosition);
  float fade=smoothstep(100.,15.,camDist);
  if(vNodeType>.5){col*=1.1;alpha*=.9;}
  col*=(1.+vGlow*.1);
  gl_FragColor=vec4(col,alpha*fade);
}`;

const connVertexShader = `${noiseFunctions}
attribute vec3 startPoint;
attribute vec3 endPoint;
attribute float connectionStrength;
attribute float pathIndex;
attribute vec3 connectionColor;
uniform float uTime;
uniform vec3 uPulsePositions[3];
uniform float uPulseTimes[3];
uniform float uPulseSpeed;
varying vec3 vColor;
varying float vConnectionStrength;
varying float vPulseIntensity;
varying float vPathPosition;
varying float vDistanceFromCamera;
float getPulseIntensity(vec3 worldPos,vec3 pulsePos,float pulseTime){
  if(pulseTime<0.)return 0.;
  float age=uTime-pulseTime;
  if(age<0.||age>4.)return 0.;
  float radius=age*uPulseSpeed;
  float d=distance(worldPos,pulsePos);
  return smoothstep(3.,0.,abs(d-radius))*smoothstep(4.,0.,age);
}
void main(){
  float t=position.x;vPathPosition=t;
  vec3 mid=mix(startPoint,endPoint,.5);
  float offset=sin(t*3.14159)*.15;
  vec3 perp=normalize(cross(normalize(endPoint-startPoint),vec3(0.,1.,0.)));
  if(length(perp)<.1)perp=vec3(1.,0.,0.);
  mid+=perp*offset;
  vec3 p0=mix(startPoint,mid,t);vec3 p1=mix(mid,endPoint,t);
  vec3 finalPos=mix(p0,p1,t);
  float noise=snoise(vec3(pathIndex*.08,t*.6,uTime*.15));
  finalPos+=perp*noise*.12;
  vec3 worldPos=(modelMatrix*vec4(finalPos,1.)).xyz;
  float total=0.;
  for(int i=0;i<3;i++) total+=getPulseIntensity(worldPos,uPulsePositions[i],uPulseTimes[i]);
  vPulseIntensity=min(total,1.);
  vColor=connectionColor;vConnectionStrength=connectionStrength;
  vDistanceFromCamera=length(worldPos-cameraPosition);
  gl_Position=projectionMatrix*modelViewMatrix*vec4(finalPos,1.);
}`;

const connFragmentShader = `
uniform float uTime;
uniform vec3 uPulseColors[3];
varying vec3 vColor;
varying float vConnectionStrength;
varying float vPulseIntensity;
varying float vPathPosition;
varying float vDistanceFromCamera;
void main(){
  float f1=sin(vPathPosition*25.-uTime*4.)*.5+.5;
  float f2=sin(vPathPosition*15.-uTime*2.5+1.57)*.5+.5;
  float flow=(f1+f2*.5)/1.5;
  vec3 col=vColor*(.8+.2*sin(uTime*.6+vPathPosition*12.));
  float fi=.4*flow*vConnectionStrength;
  if(vPulseIntensity>0.){
    vec3 pc=mix(vec3(1.),uPulseColors[0],.3);
    col=mix(col,pc*1.2,vPulseIntensity*.7);
    fi+=vPulseIntensity*.8;
  }
  col*=(.7+fi+vConnectionStrength*.5);
  float alpha=.7*vConnectionStrength+flow*.3;
  alpha=mix(alpha,min(1.,alpha*2.5),vPulseIntensity);
  float fade=smoothstep(100.,15.,vDistanceFromCamera);
  gl_FragColor=vec4(col,alpha*fade);
}`;

const COLOR_PALETTES = [
    [new THREE.Color(0x667eea),new THREE.Color(0x764ba2),new THREE.Color(0xf093fb),new THREE.Color(0x9d50bb),new THREE.Color(0x6e48aa)],
    [new THREE.Color(0xf857a6),new THREE.Color(0xff5858),new THREE.Color(0xfeca57),new THREE.Color(0xff6348),new THREE.Color(0xff9068)],
    [new THREE.Color(0x4facfe),new THREE.Color(0x00f2fe),new THREE.Color(0x43e97b),new THREE.Color(0x38f9d7),new THREE.Color(0x4484ce)],
];

class NeuralNode {
    constructor(position, level = 0, type = 0) {
        this.position = position;
        this.connections = [];
        this.level = level;
        this.type = type;
        this.size = type === 0 ? THREE.MathUtils.randFloat(0.8, 1.4) : THREE.MathUtils.randFloat(0.5, 1.0);
        this.distanceFromRoot = 0;
    }

    addConnection(node, strength = 1.0) {
        if (!this.isConnectedTo(node)) {
            this.connections.push({ node, strength });
            node.connections.push({ node: this, strength });
        }
    }

    isConnectedTo(node) {
        return this.connections.some(c => c.node === node);
    }
}

function generateCrystallineSphere(density, connectivity = 1.0) {
    const nodes = [];
    const root = new NeuralNode(new THREE.Vector3(0, 0, 0), 0, 0);
    root.size = 2.0;
    nodes.push(root);
    const goldenRatio = (1 + Math.sqrt(5)) / 2;

    for (let layer = 1; layer <= 5; layer++) {
        const radius = layer * 4;
        const count = Math.floor(layer * 12 * density);
        for (let i = 0; i < count; i++) {
            const phi = Math.acos(1 - 2 * (i + 0.5) / count);
            const theta = 2 * Math.PI * i / goldenRatio;
            const pos = new THREE.Vector3(
                radius * Math.sin(phi) * Math.cos(theta),
                radius * Math.sin(phi) * Math.sin(theta),
                radius * Math.cos(phi)
            );
            const isLeaf = layer === 5 || Math.random() < 0.3;
            const node = new NeuralNode(pos, layer, isLeaf ? 1 : 0);
            node.distanceFromRoot = radius;
            nodes.push(node);
        }
    }

    const maxParents = Math.max(1, Math.round(3 * connectivity));
    const maxPeers = Math.max(1, Math.round(5 * connectivity));
    const maxLongRange = Math.round(20 * connectivity);
    const peerThreshold = connectivity > 1 ? Infinity : 0.8;

    for (let i = 1; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.level === 1) { root.addConnection(n, 0.9); continue; }
        const prev = nodes.filter(m => m.level === n.level - 1 && m !== root)
            .sort((a, b) => n.position.distanceTo(a.position) - n.position.distanceTo(b.position));
        for (let j = 0; j < Math.min(maxParents, prev.length); j++) {
            const d = n.position.distanceTo(prev[j].position);
            n.addConnection(prev[j], Math.max(0.3, 1 - d / (n.distanceFromRoot * 2)));
        }
        const same = nodes.filter((m, idx) => m.level === n.level && nodes[idx] !== n)
            .sort((a, b) => n.position.distanceTo(a.position) - n.position.distanceTo(b.position));
        for (const s of same.slice(0, maxPeers)) {
            if (n.position.distanceTo(s.position) < n.distanceFromRoot * peerThreshold) n.addConnection(s, 0.6);
        }
    }

    const outer = nodes.filter(n => n.level >= 3);
    for (let i = 0; i < Math.min(maxLongRange, outer.length * outer.length); i++) {
        const a = outer[Math.floor(Math.random() * outer.length)];
        const b = outer[Math.floor(Math.random() * outer.length)];
        if (a !== b && !a.isConnectedTo(b) && Math.abs(a.level - b.level) > 1) a.addConnection(b, 0.4);
    }

    return { nodes, rootNode: root };
}

function buildMeshes(gl, network, palette, pulseUniforms) {
    const { nodes } = network;

    const nodePositions = [], nodeTypes = [], nodeSizes = [], nodeColors = [], nodeDists = [];
    nodes.forEach(n => {
        nodePositions.push(n.position.x, n.position.y, n.position.z);
        nodeTypes.push(n.type);
        nodeSizes.push(n.size);
        nodeDists.push(n.distanceFromRoot);
        const base = palette[Math.min(n.level, palette.length - 1)].clone();
        base.offsetHSL(THREE.MathUtils.randFloatSpread(0.03), THREE.MathUtils.randFloatSpread(0.08), THREE.MathUtils.randFloatSpread(0.08));
        nodeColors.push(base.r, base.g, base.b);
    });

    const nodeGeo = new THREE.BufferGeometry();
    nodeGeo.setAttribute('position', new THREE.Float32BufferAttribute(nodePositions, 3));
    nodeGeo.setAttribute('nodeType', new THREE.Float32BufferAttribute(nodeTypes, 1));
    nodeGeo.setAttribute('nodeSize', new THREE.Float32BufferAttribute(nodeSizes, 1));
    nodeGeo.setAttribute('nodeColor', new THREE.Float32BufferAttribute(nodeColors, 3));
    nodeGeo.setAttribute('distanceFromRoot', new THREE.Float32BufferAttribute(nodeDists, 1));

    const nodeMat = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.clone(pulseUniforms),
        vertexShader: nodeVertexShader,
        fragmentShader: nodeFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });

    const nodesMesh = new THREE.Points(nodeGeo, nodeMat);

    const connPositions = [], connStarts = [], connEnds = [], connStrengths = [], connPathIdx = [], connColors = [];
    const seen = new Set();
    let pathIndex = 0;
    const SEGS = 20;

    nodes.forEach((node, ni) => {
        node.connections.forEach(conn => {
            const ci = nodes.indexOf(conn.node);
            if (ci === -1) return;
            const key = `${Math.min(ni, ci)}-${Math.max(ni, ci)}`;
            if (seen.has(key)) return;
            seen.add(key);
            const avgLevel = Math.min(Math.floor((node.level + conn.node.level) / 2), palette.length - 1);
            const base = palette[avgLevel].clone();
            base.offsetHSL(THREE.MathUtils.randFloatSpread(0.03), THREE.MathUtils.randFloatSpread(0.08), THREE.MathUtils.randFloatSpread(0.08));
            for (let i = 0; i < SEGS; i++) {
                connPositions.push(i / (SEGS - 1), 0, 0);
                connStarts.push(node.position.x, node.position.y, node.position.z);
                connEnds.push(conn.node.position.x, conn.node.position.y, conn.node.position.z);
                connStrengths.push(conn.strength);
                connPathIdx.push(pathIndex);
                connColors.push(base.r, base.g, base.b);
            }
            pathIndex++;
        });
    });

    const connGeo = new THREE.BufferGeometry();
    connGeo.setAttribute('position', new THREE.Float32BufferAttribute(connPositions, 3));
    connGeo.setAttribute('startPoint', new THREE.Float32BufferAttribute(connStarts, 3));
    connGeo.setAttribute('endPoint', new THREE.Float32BufferAttribute(connEnds, 3));
    connGeo.setAttribute('connectionStrength', new THREE.Float32BufferAttribute(connStrengths, 1));
    connGeo.setAttribute('pathIndex', new THREE.Float32BufferAttribute(connPathIdx, 1));
    connGeo.setAttribute('connectionColor', new THREE.Float32BufferAttribute(connColors, 3));

    const connMat = new THREE.ShaderMaterial({
        uniforms: THREE.UniformsUtils.clone(pulseUniforms),
        vertexShader: connVertexShader,
        fragmentShader: connFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });

    const connMesh = new THREE.LineSegments(connGeo, connMat);

    return { nodesMesh, nodeMat, connMesh, connMat };
}

function createStarfield(gl) {
    const count = 8000;
    const positions = [], colors = [], sizes = [];
    for (let i = 0; i < count; i++) {
        const r = THREE.MathUtils.randFloat(50, 150);
        const phi = Math.acos(THREE.MathUtils.randFloatSpread(2));
        const theta = THREE.MathUtils.randFloat(0, Math.PI * 2);
        positions.push(r * Math.sin(phi) * Math.cos(theta), r * Math.sin(phi) * Math.sin(theta), r * Math.cos(phi));
        const c = Math.random();
        if (c < 0.7) colors.push(1, 1, 1);
        else if (c < 0.85) colors.push(0.7, 0.8, 1);
        else colors.push(1, 0.9, 0.8);
        sizes.push(THREE.MathUtils.randFloat(0.1, 0.3));
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));
    const mat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 } },
        vertexShader: `
            attribute float size; attribute vec3 color; varying vec3 vColor; uniform float uTime;
            void main(){ vColor=color; vec4 mv=modelViewMatrix*vec4(position,1.);
            float t=sin(uTime*2.+position.x*100.)*.3+.7;
            gl_PointSize=size*t*(300./-mv.z); gl_Position=projectionMatrix*mv; }`,
        fragmentShader: `
            varying vec3 vColor;
            void main(){ vec2 c=gl_PointCoord-.5; if(length(c)>.5)discard;
            float a=1.-smoothstep(0.,.5,length(c)); gl_FragColor=vec4(vColor,a*.8); }`,
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    return new THREE.Points(geo, mat);
}

export function initNeuralBackground(canvas, options = {}) {
    if (!canvas) return null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    let animId, resizeHandler;

    try {
        const paletteIndex = options.paletteIndex ?? 2;
        const density = options.density ?? 1.0;
        const palette = COLOR_PALETTES[paletteIndex % COLOR_PALETTES.length];

        const w = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth;
        const h = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight;

        const scene = new THREE.Scene();
        scene.fog = new THREE.FogExp2(0x000000, 0.002);

        const camera = new THREE.PerspectiveCamera(65, w / h, 0.1, 1000);
        camera.position.set(0, 8, options.zoom ?? 38);

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
        renderer.setSize(w, h);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setClearColor(0x000000);
        renderer.outputColorSpace = THREE.SRGBColorSpace;

        const starfield = createStarfield();
        scene.add(starfield);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.rotateSpeed = 0.6;
        controls.minDistance = 8;
        controls.maxDistance = 80;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.2;
        controls.enablePan = false;
        controls.touches = {
            ONE: null,
            TWO: THREE.TOUCH.DOLLY_ROTATE,
        };
        renderer.domElement.style.touchAction = 'pan-y';

        const composer = new EffectComposer(renderer);
        composer.addPass(new RenderPass(scene, camera));
        const bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), 1.8, 0.6, 0.7);
        composer.addPass(bloomPass);
        composer.addPass(new OutputPass());

        const pulseUniforms = {
            uTime: { value: 0.0 },
            uPulsePositions: { value: [new THREE.Vector3(1e4, 1e4, 1e4), new THREE.Vector3(1e4, 1e4, 1e4), new THREE.Vector3(1e4, 1e4, 1e4)] },
            uPulseTimes: { value: [-1e4, -1e4, -1e4] },
            uPulseColors: { value: [new THREE.Color(1, 1, 1), new THREE.Color(1, 1, 1), new THREE.Color(1, 1, 1)] },
            uPulseSpeed: { value: 18.0 },
            uBaseNodeSize: { value: 0.6 },
        };

        let currentConnectivity = options.connectivity ?? 1.0;
        let currentMeshes = spawnMeshes(density, currentConnectivity);

        function spawnMeshes(d, c) {
            const network = generateCrystallineSphere(d, c);
            const { nodesMesh, nodeMat, connMesh, connMat } = buildMeshes(scene, network, palette, pulseUniforms);
            scene.add(nodesMesh);
            scene.add(connMesh);
            palette.forEach((color, i) => {
                if (i < 3) {
                    nodeMat.uniforms.uPulseColors.value[i].copy(color);
                    connMat.uniforms.uPulseColors.value[i].copy(color);
                }
            });
            return { nodesMesh, nodeMat, connMesh, connMat };
        }

        function setDensity(newDensity, newConnectivity = currentConnectivity) {
            scene.remove(currentMeshes.nodesMesh);
            scene.remove(currentMeshes.connMesh);
            currentMeshes.nodesMesh.geometry.dispose();
            currentMeshes.nodeMat.dispose();
            currentMeshes.connMesh.geometry.dispose();
            currentMeshes.connMat.dispose();
            currentConnectivity = Math.max(0.1, newConnectivity);
            currentMeshes = spawnMeshes(Math.max(0.1, newDensity), currentConnectivity);
        }

        let pulseIdx = 0;
        const raycaster = new THREE.Raycaster();
        const pointer = new THREE.Vector2();
        const interactionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const interactionPoint = new THREE.Vector3();

        const clickHandler = (e) => {
            const { nodeMat, connMat } = currentMeshes;
            const rect = canvas.getBoundingClientRect();
            pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(pointer, camera);
            interactionPlane.normal.copy(camera.position).normalize();
            interactionPlane.constant = -interactionPlane.normal.dot(camera.position) + camera.position.length() * 0.5;
            if (raycaster.ray.intersectPlane(interactionPlane, interactionPoint)) {
                const idx = pulseIdx % 3;
                pulseIdx++;
                nodeMat.uniforms.uPulsePositions.value[idx].copy(interactionPoint);
                nodeMat.uniforms.uPulseTimes.value[idx] = nodeMat.uniforms.uTime.value;
                connMat.uniforms.uPulsePositions.value[idx].copy(interactionPoint);
                connMat.uniforms.uPulseTimes.value[idx] = connMat.uniforms.uTime.value;
                const color = new THREE.Color().setHSL(Math.random(), 0.8, 0.6);
                nodeMat.uniforms.uPulseColors.value[idx].copy(color);
                connMat.uniforms.uPulseColors.value[idx].copy(color);
            }
        };

        const livewirePulseHandler = () => {
            const rect = canvas.getBoundingClientRect();
            clickHandler({ clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 });

            options.connectivity += 0.5;

            setTimeout(() => {
                setDensity(options.density, options.connectivity);
                zoomHandler();
            }, 2000);
        };
        window.addEventListener('neural-pulse', livewirePulseHandler);

        const zoomHandler = () => {
            options.zoom -= 0.5;

            const duration= 1000;
            const start= camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
            const startTime= performance.now();

            function zoomAnimation() {
                const elapsed = performance.now() - startTime;
                const t = Math.min(elapsed / duration, 1);
                const newZoom = THREE.MathUtils.lerp(start, options.zoom, t);
                camera.position.setLength(newZoom);
                if (t < 1) {
                    requestAnimationFrame(zoomAnimation);
                }
            }

            zoomAnimation();
        }

        resizeHandler = () => {
            const nw = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth;
            const nh = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight;
            camera.aspect = nw / nh;
            camera.updateProjectionMatrix();
            renderer.setSize(nw, nh);
            composer.setSize(nw, nh);
            bloomPass.resolution.set(nw, nh);
        };
        window.addEventListener('resize', resizeHandler);

        const clock = new THREE.Clock();

        const animate = () => {
            animId = requestAnimationFrame(animate);
            const t = clock.getElapsedTime();
            const { nodeMat, connMat, nodesMesh, connMesh } = currentMeshes;
            nodeMat.uniforms.uTime.value = t;
            connMat.uniforms.uTime.value = t;
            starfield.rotation.y += 0.0002;
            starfield.material.uniforms.uTime.value = t;
            nodesMesh.rotation.y = Math.sin(t * 0.04) * 0.05;
            connMesh.rotation.y = Math.sin(t * 0.04) * 0.05;
            controls.update();
            composer.render();
        };
        animate();

        canvas.dataset.neuralReady = '1';

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('neural-pulse', livewirePulseHandler);
            controls.dispose();
            renderer.dispose();
        };

    } catch (err) {
        console.warn('Neural background: failed to initialise', err);
        canvas.dataset.neuralFailed = '1';
        return null;
    }
}

export default function neuralBackground(defaultOptions = {}) {
    return {
        ready: false,
        failed: false,
        showTwoFingerHint: false,
        _destroy: null,
        _hintTimeout: null,
        _touchHandlers: null,

        init() {
            const canvas = this.$refs.neuralCanvas;
            if (!canvas) { this.failed = true; return; }

            const options = {
                paletteIndex: Number(this.$el.dataset.paletteIndex ?? defaultOptions.paletteIndex ?? 2),
                density: Number(this.$el.dataset.density ?? defaultOptions.density ?? 1.0),
                connectivity: Number(this.$el.dataset.connectivity ?? defaultOptions.connectivity ?? 1.0),
                zoom: Number(this.$el.dataset.zoom ?? defaultOptions.zoom ?? 38),
            };

            this._destroy = initNeuralBackground(canvas, options);
            this.ready = this._destroy !== null;
            this.failed = !this.ready;

            if (this.ready) {
                this.setupTwoFingerHint(canvas);
            }
        },

        setupTwoFingerHint(canvas) {
            const onTouchMove = (e) => {
                if (e.touches.length === 1) {
                    this.showTwoFingerHint = true;
                    clearTimeout(this._hintTimeout);
                    this._hintTimeout = setTimeout(() => { this.showTwoFingerHint = false; }, 1200);
                } else {
                    clearTimeout(this._hintTimeout);
                    this.showTwoFingerHint = false;
                }
            };
            const onTouchEnd = () => {
                clearTimeout(this._hintTimeout);
                this._hintTimeout = setTimeout(() => { this.showTwoFingerHint = false; }, 500);
            };

            canvas.addEventListener('touchmove', onTouchMove, { passive: true });
            canvas.addEventListener('touchend', onTouchEnd, { passive: true });
            this._touchHandlers = { canvas, onTouchMove, onTouchEnd };
        },

        destroy() {
            this._destroy?.();
            clearTimeout(this._hintTimeout);

            if (this._touchHandlers) {
                const { canvas, onTouchMove, onTouchEnd } = this._touchHandlers;
                canvas.removeEventListener('touchmove', onTouchMove);
                canvas.removeEventListener('touchend', onTouchEnd);
            }
        },
    };
}
